import Transport, { TransportStatusError, StatusCodes } from '@ledgerhq/hw-transport';
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import { WalletDeviceApi } from '@shared/types/api';
import { LedgerStatus } from '@shared/types/entities/ledger';
import { merkleizeMetadata } from '@polkadot-api/merkleize-metadata';
import { SignerPayloadJSON } from '@polkadot/types/types';
import { u8aToHex, u8aToU8a } from '@polkadot/util';
import { PolkadotGenericApp } from '@zondax/ledger-substrate';
import { Mutex } from 'async-mutex';
import ewxPalletService from './EWXPalletService';
import { useLedgerStore } from '@ewf/stores/ledger';
const mutex = new Mutex();

class LedgerEWXWebHID {
  /**
   * Emit ledger status update to the Zustand store
   * This updates the UI in ConnectLedger component
   */
  private emitLedgerStatus(status: LedgerStatus): void {
    useLedgerStore.setState({ status });
  }

  private async fetchAndMerkleizeMetadata(payload: SignerPayloadJSON): Promise<{
    extrinsicPayload: any;
    metadataHash: Uint8Array;
    txMetadata: Uint8Array;
  }> {
    const metadata = await ewxPalletService.api.call.metadata.metadataAtVersion(15);

    if (!metadata) {
      throw new Error('Failed to fetch metadata');
    }

    if (!payload.signedExtensions.includes('CheckMetadataHash')) {
      payload.signedExtensions.push('CheckMetadataHash');
      payload.withSignedTransaction = true;
    }

    const { specName, specVersion } = ewxPalletService.api.runtimeVersion;
    const merkleizedMetadata = merkleizeMetadata(metadata.toHex(), {
      base58Prefix: (ewxPalletService.api.consts.system.ss58Prefix as any).toNumber(),
      decimals: ewxPalletService.api.registry.chainDecimals[0],
      specName: specName.toString(),
      specVersion: specVersion.toNumber(),
      tokenSymbol: ewxPalletService.api.registry.chainTokens[0],
    });

    const metadataHash = merkleizedMetadata.digest();
    const newPayload = { ...payload, metadataHash: u8aToHex(metadataHash), mode: 1 };
    const extrinsicPayload = ewxPalletService.api.registry.createType(
      'ExtrinsicPayload',
      newPayload,
    );
    const txMetadata = merkleizedMetadata.getProofForExtrinsicPayload(
      u8aToHex(extrinsicPayload.toU8a(true)),
    );
    return {
      extrinsicPayload,
      metadataHash,
      txMetadata: u8aToU8a(txMetadata),
    };
  }

  private async createLedgerTransport(transport: Transport | undefined): Promise<Transport> {
    if (transport) {
      return transport;
    }
    // Retry logic for device connection issues
    let retries = 3;
    while (retries > 0) {
      try {
        const newTransport = await TransportWebHID.create();
        return newTransport;
      } catch (error) {
        console.error(`Failed to create WebHID transport (${4 - retries}/3):`, error);
        retries--;

        if (retries === 0) {
          throw new Error(
            'Could not connect to Ledger device. Please ensure it is connected, unlocked, and the Polkadot app is open.',
          );
        }

        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    // This should never be reached, but TypeScript needs it
    throw new Error('Failed to create transport');
  }

  private async openApp(name: string): Promise<void> {
    let transport = await TransportWebHID.create();
    const closeAppIfOpen = await transport.send(0xb0, 0xa7, 0x00, 0x00, undefined, [
      StatusCodes.OK,
      StatusCodes.LOCKED_DEVICE,
      StatusCodes.UNKNOWN_APDU,
      StatusCodes.USER_REFUSED_ON_DEVICE,
    ]);

    const status = closeAppIfOpen.readUInt16BE(closeAppIfOpen.length - 2);
    if (status === StatusCodes.OK) {
      await transport.close();
      await new Promise((s) => setTimeout(s, 3000));
      transport = await TransportWebHID.create();
    }
    await transport.send(0xe0, 0xd8, 0x00, 0x00, Buffer.from(name, 'ascii'));
    await transport.close();
    await new Promise((s) => setTimeout(s, 3000));
  }

  public async sign(
    unsignedTx: Uint8Array,
    _path: string = "m/44'/354'/0'/0'/0'",
  ): Promise<WalletDeviceApi.Sign> {
    let transport: Transport | undefined;
    try {
      await this.openApp('Polkadot');

      if (!transport) {
        transport = await this.createLedgerTransport(transport);
      }
      // Type cast needed due to nested dependency versions (@zondax/ledger-substrate uses different @ledgerhq packages), but compatible at runtime
      const app = new PolkadotGenericApp(transport! as any);
      const { extrinsicPayload, txMetadata } = await this.fetchAndMerkleizeMetadata(
        JSON.parse(new TextDecoder().decode(unsignedTx)),
      );
      const response = await app.signWithMetadata(
        _path,
        extrinsicPayload.toU8a(true),
        Buffer.from(txMetadata),
      );

      return {
        signature: new Uint8Array(response.signature),
        extrinsicPayload: extrinsicPayload.toHex(),
      };
    } catch (e: any) {
      let errorMessage = '';
      if (Object.prototype.hasOwnProperty.call(e, 'statusText')) {
        console.error(e.statusText);
        errorMessage = e.statusText;
      } else if (Object.prototype.hasOwnProperty.call(e, 'errorMessage')) {
        console.error(e.errorMessage);
        errorMessage = e.errorMessage;
      } else {
        errorMessage = JSON.stringify(e);
      }
      throw new Error(errorMessage);
    } finally {
      if (transport) {
        try {
          await transport.send(0xb0, 0xa7, 0x00, 0x00);
          await transport.close();
        } catch (error) {
          console.error(error);
        }
      }
    }
  }

  public async signRaw(
    message: Uint8Array,
    _path: string = "m/44'/354'/0'/0'/0'",
  ): Promise<WalletDeviceApi.Sign> {
    let transport: Transport | undefined;
    try {
      await this.openApp('Polkadot');

      if (!transport) {
        transport = await this.createLedgerTransport(transport);
      }
      // Type cast needed due to nested dependency versions (@zondax/ledger-substrate uses different @ledgerhq packages), but compatible at runtime
      const app = new PolkadotGenericApp(transport! as any);

      // The message to sign (e.g., the EWX address for KYC verification)
      const messageString = new TextDecoder().decode(message);

      // Wrap in <Bytes> tag - required by Ledger Polkadot app for raw message signing
      const wrappedMessage = `<Bytes>${messageString}</Bytes>`;
      const messageBytes = new TextEncoder().encode(wrappedMessage);

      console.log('Signing raw message with Ledger', {
        originalMessage: messageString,
        wrappedMessage,
        messageLength: messageBytes.length,
      });

      const response = await app.signRaw(_path, Buffer.from(messageBytes));

      // The Ledger returns a 65-byte signature where the first byte is the signature type prefix
      // (0x00 = ed25519, 0x01 = sr25519). Need to strip this prefix and return only the
      // 64-byte raw signature to match the expected format.
      const signatureBytes = new Uint8Array(response.signature);
      const rawSignature = signatureBytes.slice(1); // Skip the first byte (type prefix)

      const signatureHex = ('0x' +
        Array.from(rawSignature)
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')) as `0x${string}`;

      console.log('Ledger signature', {
        fullLength: signatureBytes.length,
        typePrefix: signatureBytes[0],
        rawLength: rawSignature.length,
        signatureHex,
      });

      return {
        signature: signatureHex,
      };
    } catch (e: any) {
      let errorMessage = '';
      if (Object.prototype.hasOwnProperty.call(e, 'statusText')) {
        console.error(e.statusText);
        errorMessage = e.statusText;
      } else if (Object.prototype.hasOwnProperty.call(e, 'errorMessage')) {
        console.error(e.errorMessage);
        errorMessage = e.errorMessage;
      } else {
        console.error('Unknown error during signRaw', e);
        errorMessage = e.message || JSON.stringify(e);
      }
      throw new Error(errorMessage);
    } finally {
      if (transport) {
        try {
          await transport.send(0xb0, 0xa7, 0x00, 0x00);
          await transport.close();
        } catch (error) {
          console.error(error);
        }
      }
    }
  }

  public async connect(path: string = "m/44'/354'/0'/0'/0'"): Promise<WalletDeviceApi.Connect> {
    const release = await mutex.acquire();
    try {
      let errorStatus;
      let transport: Transport | undefined;
      let address = '';
      let availableBalance;
      try {
        this.emitLedgerStatus('open-application');
        await this.openApp('Polkadot');

        this.emitLedgerStatus('connecting');
        transport = await this.createLedgerTransport(transport);
        // Type cast needed due to nested dependency versions (@zondax/ledger-substrate uses different @ledgerhq packages), but compatible at runtime
        const chain = new PolkadotGenericApp(transport! as any);

        this.emitLedgerStatus('verify-address');
        const r = await chain.getAddress(
          path,
          (ewxPalletService.api.consts.system.ss58Prefix as any).toNumber(),
          true,
        );
        address = r.address;
        const balance = await ewxPalletService.api.query.system.account<any>(address);

        availableBalance = BigInt(balance.data.free.toBn()) - BigInt(balance.data.frozen.toBn());
      } catch (error: TransportStatusError | any) {
        console.error(error);
        errorStatus =
          error instanceof TransportStatusError
            ? error.statusText
            : error.message
              ? error.message
              : 'Error connecting to Ledger';
      } finally {
        if (transport) {
          try {
            await transport.send(0xb0, 0xa7, 0x00, 0x00);
            await transport.close();
          } catch (error) {
            console.error(error);
          }
        }
      }
      return { address, balance: availableBalance?.toString() || '0', path, errorStatus };
    } finally {
      release();
    }
  }

  public async isDevicePlugged(): Promise<WalletDeviceApi.IsDevicePlugged> {
    const release = await mutex.acquire();
    try {
      // First check if we have previously authorized devices
      const devices = await TransportWebHID.list();
      if (devices.length > 0) {
        return true;
      }

      // If no authorized devices, try to create a transport
      // This will trigger the browser's permission dialog if needed
      // and properly detect if a device is connected
      let transport: Transport | undefined;
      try {
        transport = await TransportWebHID.create();
        // If we get here, device is connected and authorized
        return true;
      } catch (error: any) {
        // Check if error indicates no device vs other errors
        // "No device selected" means user cancelled permission dialog
        // "Access denied" means permission was denied
        // Other errors might indicate device issues
        console.log('isDevicePlugged transport error:', error?.message || error);
        return false;
      } finally {
        if (transport) {
          try {
            await transport.close();
          } catch (e) {
            // Ignore close errors
          }
        }
      }
    } finally {
      release();
    }
  }

  public async isDeviceLocked(): Promise<WalletDeviceApi.IsDeviceLocked> {
    const release = await mutex.acquire();
    try {
      let returnValue = true;
      try {
        returnValue = await this.checkDevice(returnValue);
      } catch (error) {
        console.log(error);
      }
      return returnValue;
    } finally {
      release();
    }
  }

  public async checkDevice(returnValue: boolean): Promise<WalletDeviceApi.CheckDevice> {
    try {
      const transport = await TransportWebHID.create();
      const res = await transport.send(0xb0, 0x01, 0x00, 0x00, undefined, [
        StatusCodes.OK,
        StatusCodes.LOCKED_DEVICE,
        StatusCodes.UNKNOWN_APDU,
      ]);

      const status = res.readUInt16BE(res.length - 2);
      if (status !== StatusCodes.LOCKED_DEVICE) {
        returnValue = false;
      }
      await transport.close();
      return returnValue;
    } catch (error) {
      console.error(error);
      await new Promise((s) => setTimeout(s, 1000));
      return await this.checkDevice(returnValue);
    }
  }
}

const ledgerEWXWebHID = new LedgerEWXWebHID();
export default ledgerEWXWebHID;
